import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Plus, Check, X, Plane, CalendarOff, PartyPopper, Trash2, Pencil, Moon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const typeMeta = {
  vacaciones: { label: 'Vacaciones', icon: Plane },
  dia_libre: { label: 'Día libre', icon: CalendarOff },
};

const statusMeta = {
  pendiente: { label: 'Pendiente', cls: 'bg-orange-100 text-orange-700' },
  aprobada: { label: 'Aprobada', cls: 'bg-green-100 text-green-700' },
  rechazada: { label: 'Rechazada', cls: 'bg-red-100 text-red-700' },
};

export default function PersonalCalendario({ isAdmin, employees }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [data, setData] = useState({ leaves: [], holidays: [], rest_days: [] });
  const [pending, setPending] = useState([]);
  const [balanceEmp, setBalanceEmp] = useState('');
  const [balance, setBalance] = useState(null);

  // Request dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: 'vacaciones', date_from: '', date_to: '', reason: '', employee_id: '' });
  const [saving, setSaving] = useState(false);

  // Admin: all requests management
  const [allReqs, setAllReqs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('todas');
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const loadCalendar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/personal/calendar?month=${month}`);
      setData({ leaves: [], holidays: [], rest_days: [], ...(res.data || {}) });
    } catch { /* noop */ }
  }, [month]);

  const loadPending = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/personal/leave-requests?status=pendiente`);
      setPending(res.data || []);
    } catch { /* noop */ }
  }, [isAdmin]);

  const loadBalance = useCallback(async (empId) => {
    try {
      const params = empId ? `?employee_id=${empId}` : '';
      const res = await axios.get(`${API}/personal/vacation-balance${params}`);
      setBalance(res.data);
    } catch { setBalance(null); }
  }, []);

  const loadAll = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/personal/leave-requests`);
      setAllReqs(res.data || []);
    } catch { /* noop */ }
  }, [isAdmin]);

  const refreshAll = useCallback(() => {
    loadCalendar(); loadPending(); loadAll();
    if (isAdmin) { if (balanceEmp) loadBalance(balanceEmp); }
    else loadBalance('');
  }, [loadCalendar, loadPending, loadAll, isAdmin, balanceEmp, loadBalance]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);
  useEffect(() => { loadPending(); }, [loadPending]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (isAdmin) {
      if (!balanceEmp && employees.length > 0) { setBalanceEmp(employees[0].id); return; }
      if (balanceEmp) loadBalance(balanceEmp);
    } else {
      loadBalance('');
    }
  }, [isAdmin, balanceEmp, employees, loadBalance]);

  const changeMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const cells = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const startOffset = (new Date(y, m - 1, 1).getDay() + 6) % 7;
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(`${month}-${String(d).padStart(2, '0')}`);
    return arr;
  }, [month]);

  const eventsForDay = (iso) => {
    const leaves = data.leaves.filter(l => l.date_from <= iso && iso <= l.date_to);
    const holiday = data.holidays.find(h => h.date === iso);
    const wd = (new Date(iso + 'T00:00:00').getDay() + 6) % 7;
    const onLeaveIds = new Set(leaves.map(l => l.employee_id));
    const resting = (data.rest_days || []).filter(r => r.weekly_rest_days.includes(wd) && !onLeaveIds.has(r.employee_id));
    return { leaves, holiday, resting };
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!form.date_from || !form.date_to) { toast.error('Selecciona las fechas'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isAdmin && form.employee_id) payload.employee_id = form.employee_id;
      else delete payload.employee_id;
      await axios.post(`${API}/personal/leave-requests`, payload);
      toast.success('Solicitud enviada');
      setOpen(false);
      setForm({ type: 'vacaciones', date_from: '', date_to: '', reason: '', employee_id: '' });
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setSaving(false);
    }
  };

  const showWarnings = (warnings) => {
    if (warnings?.length) toast.warning(warnings[0] + (warnings.length > 1 ? ` (+${warnings.length - 1} días más)` : ''));
  };

  const review = async (id, action, comment) => {
    try {
      let res;
      if (action === 'approve') res = await axios.put(`${API}/personal/leave-requests/${id}/approve`);
      else res = await axios.put(`${API}/personal/leave-requests/${id}/reject`, { review_comment: comment || 'Rechazada' });
      toast.success(action === 'approve' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      showWarnings(res.data?.min_staff_warnings);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al procesar');
    }
  };

  const openEdit = (r) => {
    setEditForm({
      id: r.id, employee_name: r.employee_name, type: r.type,
      date_from: r.date_from, date_to: r.date_to, status: r.status, reason: r.reason || '',
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.date_from || !editForm.date_to) { toast.error('Selecciona las fechas'); return; }
    setSavingEdit(true);
    try {
      const { id, employee_name, ...payload } = editForm;
      const res = await axios.put(`${API}/personal/leave-requests/${id}`, payload);
      toast.success('Solicitud actualizada');
      showWarnings(res.data?.min_staff_warnings);
      setEditForm(null);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteReq = async (r) => {
    if (!window.confirm(`¿Eliminar la solicitud de ${r.employee_name} (${r.date_from} → ${r.date_to})? El calendario y los saldos se actualizarán automáticamente.`)) return;
    try {
      await axios.delete(`${API}/personal/leave-requests/${r.id}`);
      toast.success('Solicitud eliminada');
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filteredReqs = statusFilter === 'todas' ? allReqs : allReqs.filter(r => r.status === statusFilter);

  const [y, m] = month.split('-').map(Number);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="cal-prev"><ChevronLeft size={16} /></Button>
          <span className="text-lg font-bold text-slate-900 w-44 text-center" data-testid="cal-month">{MONTHS[m - 1]} {y}</span>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="cal-next"><ChevronRight size={16} /></Button>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="request-leave-btn">
          <Plus size={16} className="mr-2" />Solicitar ausencia
        </Button>
      </div>

      {/* Balance */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          {isAdmin && (
            <Select value={balanceEmp} onValueChange={setBalanceEmp}>
              <SelectTrigger className="w-56" data-testid="balance-employee"><SelectValue placeholder="Empleado" /></SelectTrigger>
              <SelectContent>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="flex gap-6" data-testid="vacation-balance">
            <div><p className="text-xs uppercase font-bold text-slate-400">Anuales</p><p className="text-2xl font-bold text-slate-900">{balance?.annual ?? '—'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-400">Disfrutadas</p><p className="text-2xl font-bold text-orange-600">{balance?.disfrutadas ?? '—'}</p></div>
            <div><p className="text-xs uppercase font-bold text-slate-400">Disponibles</p><p className="text-2xl font-bold text-green-600">{balance?.disponibles ?? '—'}</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1"><Plane size={13} className="text-blue-500" />Vacaciones</span>
        <span className="flex items-center gap-1"><CalendarOff size={13} className="text-purple-500" />Día libre</span>
        <span className="flex items-center gap-1"><Moon size={13} className="text-slate-400" />Descanso semanal</span>
        <span className="flex items-center gap-1"><PartyPopper size={13} className="text-red-500" />Festivo</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border-2 border-dashed border-slate-400" />Pendiente</span>
      </div>

      {/* Calendar grid */}
      <Card className="border-slate-200">
        <CardContent className="p-3">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map(w => <div key={w} className="text-center text-xs font-bold text-slate-400 py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {cells.map((iso, i) => {
              if (!iso) return <div key={i} className="min-h-[84px] rounded-lg bg-slate-50/50" />;
              const { leaves, holiday, resting } = eventsForDay(iso);
              const dayNum = parseInt(iso.split('-')[2]);
              const isToday = iso === new Date().toISOString().slice(0, 10);
              const wd = new Date(y, m - 1, dayNum).getDay();
              const isWeekend = wd === 0 || wd === 6;
              const bgClass = holiday ? 'bg-red-50 border-red-100' : (isWeekend ? 'bg-slate-50 border-slate-100' : 'bg-white border-slate-100');
              return (
                <div key={i} className={`min-h-[84px] rounded-lg border p-1.5 ${bgClass}`} data-testid={`day-${iso}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold ${isToday ? 'bg-slate-900 text-white rounded-full w-5 h-5 flex items-center justify-center' : 'text-slate-500'}`}>{dayNum}</span>
                    {holiday && <PartyPopper size={12} className="text-red-500" />}
                  </div>
                  {holiday && <p className="text-[10px] text-red-600 font-medium truncate mt-0.5">{holiday.name}</p>}
                  <div className="space-y-0.5 mt-1">
                    {leaves.slice(0, 3).map(l => (
                      <div key={l.id} className={`text-[10px] rounded px-1 py-0.5 flex items-center gap-1 truncate ${l.status === 'pendiente' ? 'border border-dashed' : ''} ${isAdmin ? 'cursor-pointer hover:ring-1 hover:ring-slate-300' : ''}`}
                        style={{ backgroundColor: l.status === 'aprobada' ? `${l.color}22` : 'transparent', borderColor: l.color, color: '#334155' }}
                        title={`${l.employee_name} — ${typeMeta[l.type]?.label}${l.status === 'pendiente' ? ' (pendiente)' : ''}${isAdmin ? ' · clic para editar' : ''}`}
                        onClick={() => isAdmin && openEdit(l)}
                        data-testid={`cal-event-${l.id}`}>
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
                        <span className="truncate">{l.employee_name.split(' ')[0]}</span>
                      </div>
                    ))}
                    {leaves.length > 3 && <p className="text-[9px] text-slate-400">+{leaves.length - 3} más</p>}
                    {resting.slice(0, 2).map(r => (
                      <div key={`rest-${r.employee_id}`} className="text-[10px] rounded px-1 py-0.5 flex items-center gap-1 truncate bg-slate-100 text-slate-500"
                        title={`${r.employee_name} — Descanso semanal`} data-testid={`rest-${r.employee_id}-${iso}`}>
                        <Moon size={9} className="shrink-0" style={{ color: r.color }} />
                        <span className="truncate">{r.employee_name.split(' ')[0]}</span>
                      </div>
                    ))}
                    {resting.length > 2 && <p className="text-[9px] text-slate-400">+{resting.length - 2} descanso</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Pending requests (admin) */}
      {isAdmin && (
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-base">Solicitudes pendientes {pending.length > 0 && <Badge className="ml-2 bg-orange-100 text-orange-700">{pending.length}</Badge>}</CardTitle></CardHeader>
          <CardContent>
            {pending.length === 0 ? (
              <p className="text-sm text-slate-400">No hay solicitudes pendientes.</p>
            ) : (
              <div className="space-y-2" data-testid="pending-requests">
                {pending.map(r => {
                  const Icon = typeMeta[r.type]?.icon || Plane;
                  return (
                    <div key={r.id} className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${r.color}22` }}>
                          <Icon size={15} style={{ color: r.color }} />
                        </span>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{r.employee_name} <span className="text-xs font-normal text-slate-400">· {typeMeta[r.type]?.label}</span></p>
                          <p className="text-xs text-slate-500">{r.date_from} → {r.date_to} · {r.days_count} día(s){r.reason ? ` · ${r.reason}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" onClick={() => review(r.id, 'approve')} data-testid={`approve-${r.id}`}><Check size={14} className="mr-1" />Aprobar</Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 h-8" onClick={() => review(r.id, 'reject')} data-testid={`reject-${r.id}`}><X size={14} className="mr-1" />Rechazar</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* All requests management (admin) */}
      {isAdmin && (
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Todas las solicitudes</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-8" data-testid="req-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="pendiente">Pendientes</SelectItem>
                <SelectItem value="aprobada">Aprobadas</SelectItem>
                <SelectItem value="rechazada">Rechazadas</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {filteredReqs.length === 0 ? (
              <p className="text-sm text-slate-400">No hay solicitudes.</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="all-requests">
                {filteredReqs.map(r => {
                  const Icon = typeMeta[r.type]?.icon || Plane;
                  const sm = statusMeta[r.status] || statusMeta.pendiente;
                  return (
                    <div key={r.id} className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-slate-50" data-testid={`req-row-${r.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${r.color}22` }}>
                          <Icon size={15} style={{ color: r.color }} />
                        </span>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">
                            {r.employee_name} <span className="text-xs font-normal text-slate-400">· {typeMeta[r.type]?.label}</span>
                            <Badge className={`ml-2 ${sm.cls}`}>{sm.label}</Badge>
                          </p>
                          <p className="text-xs text-slate-500">{r.date_from} → {r.date_to} · {r.days_count} día(s){r.reason ? ` · ${r.reason}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-slate-900" onClick={() => openEdit(r)} data-testid={`edit-req-${r.id}`}><Pencil size={14} /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteReq(r)} data-testid={`delete-req-${r.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Solicitar ausencia</DialogTitle>
            <DialogDescription>Vacaciones o días libres. Un administrador aprobará la solicitud.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRequest} className="space-y-4">
            {isAdmin && (
              <div className="space-y-1.5">
                <Label>Empleado</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="req-employee"><SelectValue placeholder="Selecciona empleado" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="req-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacaciones">Vacaciones</SelectItem>
                  <SelectItem value="dia_libre">Día libre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Desde</Label>
                <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value, date_to: form.date_to || e.target.value })} data-testid="req-from" />
              </div>
              <div className="space-y-1.5">
                <Label>Hasta</Label>
                <Input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="req-to" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Motivo (opcional)</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} data-testid="req-reason" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="submit-request">
                {saving ? 'Enviando...' : 'Enviar solicitud'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog (admin) */}
      <Dialog open={!!editForm} onOpenChange={(v) => !v && setEditForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar solicitud</DialogTitle>
            <DialogDescription>{editForm?.employee_name} — los cambios actualizan el calendario y los saldos automáticamente.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tipo</Label>
                  <Select value={editForm.type} onValueChange={(v) => setEditForm({ ...editForm, type: v })}>
                    <SelectTrigger data-testid="edit-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vacaciones">Vacaciones</SelectItem>
                      <SelectItem value="dia_libre">Día libre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Estado</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="aprobada">Aprobada</SelectItem>
                      <SelectItem value="rechazada">Rechazada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Desde</Label>
                  <Input type="date" value={editForm.date_from} onChange={(e) => setEditForm({ ...editForm, date_from: e.target.value })} data-testid="edit-from" />
                </div>
                <div className="space-y-1.5">
                  <Label>Hasta</Label>
                  <Input type="date" value={editForm.date_to} onChange={(e) => setEditForm({ ...editForm, date_to: e.target.value })} data-testid="edit-to" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Motivo (opcional)</Label>
                <Textarea value={editForm.reason} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} rows={2} data-testid="edit-reason" />
              </div>
              <DialogFooter className="flex-row justify-between sm:justify-between">
                <Button type="button" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => { const r = editForm; setEditForm(null); deleteReq(r); }} data-testid="edit-delete-btn">
                  <Trash2 size={14} className="mr-1" />Eliminar
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditForm(null)}>Cancelar</Button>
                  <Button type="submit" disabled={savingEdit} className="bg-orange-500 hover:bg-orange-600" data-testid="save-edit-request">
                    {savingEdit ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
